/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Brain, Send, Plus, Trash2, Copy, RefreshCw, Zap, FileText, MessageSquare, Globe, Image, Mic } from 'lucide-react';
import { getAvatarUrl } from '../utils/helpers';
import './AIPage.css';

const SUGGESTIONS = [
  { icon:'💬', text:'Summarize my recent chats' },
  { icon:'🌍', text:'Translate a message to Hindi' },
  { icon:'📝', text:'Help me write a reply' },
  { icon:'❓', text:'Answer a question for me' },
  { icon:'🔍', text:'Explain something to me' },
  { icon:'✨', text:'Generate creative content' },
];

const CAPABILITIES = [
  { icon:MessageSquare, label:'Chat Summarizer',    desc:'Summarize long conversations',     color:'#6C63FF' },
  { icon:Globe,         label:'Instant Translator', desc:'Translate to 20+ languages',       color:'#00B4D8' },
  { icon:FileText,      label:'Doc Analyzer',       desc:'Analyze and explain documents',    color:'#FFB830' },
  { icon:Zap,           label:'Reply Generator',    desc:'Generate smart reply suggestions', color:'#00D68F' },
  { icon:Image,         label:'Image OCR',          desc:'Extract text from images',         color:'#FF4D8D' },
  { icon:Mic,           label:'Voice Assistant',    desc:'Voice-powered AI assistant',       color:'#8B5CF6' },
];

const GEMINI_KEY = process.env.REACT_APP_GEMINI_API_KEY;

async function callGemini(messages, apiKey) {
  if (!apiKey) throw new Error('NO_KEY');
  const contents = messages.map(m => ({
    role: m.role === 'assistant' ? 'model' : 'user',
    parts: [{ text: m.content }]
  }));
  const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contents, generationConfig: { temperature:0.7, maxOutputTokens:1024 } })
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const data = await res.json();
  return data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response';
}

// Fallback demo responses when no API key
const DEMO_RESPONSES = {
  default: "I'm Nexa AI, powered by Gemini! To enable full AI capabilities, add your **GEMINI_API_KEY** to the client `.env` file:\n\n```\nREACT_APP_GEMINI_API_KEY=your_key_here\n```\n\nGet a free key at [Google AI Studio](https://aistudio.google.com) 🚀",
  summarize: "Here's a summary of your recent conversations:\n\n• **Srinu K** - Discussed the new Nexa features, very positive feedback\n• **Nandini** - Talked about the weekend plans and project updates\n• **Mayank** - Shared documents and discussed deadlines\n\nOverall sentiment: Positive 😊",
  translate: "Translation to Hindi:\n\n**English:** Hello, how are you?\n**हिंदी:** नमस्ते, आप कैसे हैं?\n\nWould you like me to translate anything else?",
  reply: "Here are 3 smart reply suggestions:\n\n1. **Formal:** \"Thank you for your message. I'll review this and get back to you shortly.\"\n2. **Casual:** \"Got it! Will check and update you soon 👍\"\n3. **Detailed:** \"Thanks for the heads up! I've noted this and will work on it by tomorrow.\"\n\nWhich one would you like to use?",
};

export default function AIPage() {
  const { user }  = useAuth();
  const toast     = useToast();

  const [sessions,    setSessions]    = useState([{ id:'s1', title:'New Chat', messages:[], createdAt:new Date().toISOString() }]);
  const [activeId,    setActiveId]    = useState('s1');
  const [input,       setInput]       = useState('');
  const [loading,     setLoading]     = useState(false);

  const messagesEnd = useRef(null);
  const inputRef    = useRef(null);

  const activeSession = sessions.find(s => s.id === activeId);
  const messages      = activeSession?.messages || [];

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`nexa_ai_${user?._id||user?.id}`) || '[]');
      if (saved.length > 0) { setSessions(saved); setActiveId(saved[0].id); }
    } catch {}
  }, []);

  const saveSessions = (list) => {
    try { localStorage.setItem(`nexa_ai_${user?._id||user?.id}`, JSON.stringify(list)); } catch {}
    setSessions(list);
  };

  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  const updateSession = (id, updates) => {
    const updated = sessions.map(s => s.id===id ? {...s,...updates} : s);
    saveSessions(updated);
  };

  const addMessage = (sessionId, msg) => {
    const updated = sessions.map(s => s.id===sessionId
      ? { ...s, messages:[...s.messages, msg], title: s.messages.length===0 ? msg.content.slice(0,40) : s.title }
      : s
    );
    saveSessions(updated);
  };

  const sendMessage = async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;
    setInput('');

    const userMsg = { role:'user', content, id:`m_${Date.now()}`, time:new Date().toISOString() };
    addMessage(activeId, userMsg);
    setLoading(true);

    try {
      let reply;
      if (GEMINI_KEY) {
        const history = [...messages, userMsg].map(m => ({ role:m.role, content:m.content }));
        reply = await callGemini(history, GEMINI_KEY);
      } else {
        // Demo mode
        await new Promise(r => setTimeout(r, 1200));
        const lower = content.toLowerCase();
        if (lower.includes('summar')) reply = DEMO_RESPONSES.summarize;
        else if (lower.includes('translat')) reply = DEMO_RESPONSES.translate;
        else if (lower.includes('reply') || lower.includes('suggest')) reply = DEMO_RESPONSES.reply;
        else reply = DEMO_RESPONSES.default;
      }

      const aiMsg = { role:'assistant', content:reply, id:`m_${Date.now()}`, time:new Date().toISOString() };
      addMessage(activeId, aiMsg);
    } catch(err) {
      const errMsg = { role:'assistant', content:`❌ Error: ${err.message === 'NO_KEY' ? 'No Gemini API key configured. Add REACT_APP_GEMINI_API_KEY to your .env file.' : err.message}`, id:`m_${Date.now()}`, time:new Date().toISOString(), isError:true };
      addMessage(activeId, errMsg);
      toast.error('AI request failed');
    } finally { setLoading(false); }
  };

  const newSession = () => {
    const s = { id:`s_${Date.now()}`, title:'New Chat', messages:[], createdAt:new Date().toISOString() };
    const updated = [s, ...sessions];
    saveSessions(updated);
    setActiveId(s.id);
  };

  const deleteSession = (id) => {
    const updated = sessions.filter(s => s.id!==id);
    if (updated.length === 0) {
      const s = { id:`s_${Date.now()}`, title:'New Chat', messages:[], createdAt:new Date().toISOString() };
      saveSessions([s]); setActiveId(s.id);
    } else {
      saveSessions(updated);
      if (activeId === id) setActiveId(updated[0].id);
    }
  };

  const copyMsg = (content) => { navigator.clipboard.writeText(content); toast.success('Copied!'); };

  // Format markdown-like text
  const formatText = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code style="background:rgba(108,99,255,.15);padding:2px 6px;border-radius:4px;font-size:.85em">$1</code>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="ai-page">

      {/* ── Sessions sidebar ── */}
      <div className="ai-sidebar">
        <div className="ai-sidebar-header">
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <Brain size={18} style={{color:'var(--primary-light)'}}/>
            <h2>AI Assistant</h2>
          </div>
          <button className="btn btn-primary btn-sm" onClick={newSession}>
            <Plus size={14}/>
          </button>
        </div>

        <div className="ai-sessions">
          {sessions.map(s => (
            <div key={s.id} className={`ai-session-item ${s.id===activeId?'active':''}`} onClick={()=>setActiveId(s.id)}>
              <Brain size={14} style={{flexShrink:0,color:s.id===activeId?'var(--primary-light)':'var(--text-500)'}}/>
              <span className="ai-session-title">{s.title || 'New Chat'}</span>
              <button className="ai-session-del" onClick={e=>{e.stopPropagation();deleteSession(s.id);}}>
                <Trash2 size={11}/>
              </button>
            </div>
          ))}
        </div>

        {/* Capabilities */}
        <div className="ai-capabilities">
          <p className="ai-cap-title">Capabilities</p>
          {CAPABILITIES.map(c => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="ai-cap-item">
                <div className="ai-cap-icon" style={{background:c.color+'20',color:c.color}}>
                  <Icon size={14}/>
                </div>
                <div>
                  <p className="ai-cap-name">{c.label}</p>
                  <p className="ai-cap-desc">{c.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Chat area ── */}
      <div className="ai-main">

        {/* Header */}
        <div className="ai-header">
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div className="ai-header-icon"><Brain size={20}/></div>
            <div>
              <h2>Nexa AI</h2>
              <p className="ai-header-sub">Powered by Gemini • Always available</p>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn-icon" title="New chat" onClick={newSession}><Plus size={16}/></button>
            {messages.length > 0 && (
              <button className="btn-icon" title="Clear chat" onClick={()=>updateSession(activeId,{messages:[],title:'New Chat'})}>
                <RefreshCw size={16}/>
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="ai-messages">
          {messages.length === 0 ? (
            <div className="ai-welcome">
              <div className="ai-welcome-icon"><Brain size={40}/></div>
              <h2>How can I help you?</h2>
              <p>Ask me anything — I can summarize chats, translate messages, generate replies, answer questions, and much more.</p>

              {!GEMINI_KEY && (
                <div className="ai-demo-banner">
                  <Zap size={14}/>
                  Running in demo mode — Add REACT_APP_GEMINI_API_KEY for full AI
                </div>
              )}

              <div className="ai-suggestions">
                {SUGGESTIONS.map(s => (
                  <button key={s.text} className="ai-suggestion-btn" onClick={()=>sendMessage(s.text)}>
                    <span>{s.icon}</span>{s.text}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map(msg => (
                <div key={msg.id} className={`ai-msg-row ${msg.role}`}>
                  {msg.role === 'user' ? (
                    <img src={getAvatarUrl(user)} alt="" className="avatar" style={{width:32,height:32,flexShrink:0,alignSelf:'flex-end'}}/>
                  ) : (
                    <div className="ai-bot-avatar"><Brain size={16}/></div>
                  )}
                  <div className={`ai-bubble ${msg.role} ${msg.isError?'error':''}`}>
                    <div className="ai-bubble-content" dangerouslySetInnerHTML={{__html: formatText(msg.content)}}/>
                    <div className="ai-bubble-actions">
                      <button className="ai-copy-btn" onClick={()=>copyMsg(msg.content)} title="Copy">
                        <Copy size={11}/>
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="ai-msg-row assistant">
                  <div className="ai-bot-avatar"><Brain size={16}/></div>
                  <div className="ai-bubble assistant">
                    <div style={{display:'flex',gap:5,alignItems:'center',padding:'4px 0'}}>
                      <span className="ai-typing-dot"/>
                      <span className="ai-typing-dot"/>
                      <span className="ai-typing-dot"/>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEnd}/>
            </>
          )}
        </div>

        {/* Input */}
        <div className="ai-input-area">
          {messages.length > 0 && (
            <div className="ai-quick-actions">
              {['Summarize','Translate','Suggest reply','Explain'].map(a => (
                <button key={a} className="ai-quick-btn" onClick={()=>sendMessage(a)}>{a}</button>
              ))}
            </div>
          )}
          <div className="ai-input-row">
            <textarea
              ref={inputRef}
              className="ai-input"
              placeholder="Ask me anything..."
              value={input}
              onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage();} }}
              rows={1}
              style={{resize:'none'}}
            />
            <button className={`ai-send-btn ${input.trim()?'ready':''}`}
              onClick={()=>sendMessage()} disabled={!input.trim()||loading}>
              {loading ? <span className="loader" style={{width:16,height:16,borderWidth:2}}/> : <Send size={18}/>}
            </button>
          </div>
          <p className="ai-input-hint">Press Enter to send · Shift+Enter for new line</p>
        </div>
      </div>
    </div>
  );
}